import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AxiosError } from 'axios';
import { Eye, EyeOff, CheckCircle2, AlertTriangle } from 'lucide-react';
import { api } from '@/lib/api';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Card, CardBody, CardHeader } from '@/components/card';
import { Badge } from '@/components/badge';
import { useFormError } from '@/lib/form-error';

type InvitationInfo = {
  email: string;
  name: string;
  role: 'admin' | 'client_user';
  expires_at: string;
};

export function InvitationAccept() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [info, setInfo] = useState<InvitationInfo | null>(null);
  const [loadError, setLoadError] = useState<{ status: number; message: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState(false);
  const { error, wrap } = useFormError();

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const { data } = await api.get<InvitationInfo>(`/v1/invitations/${token}`);
        setInfo(data);
      } catch (e) {
        if (e instanceof AxiosError) {
          const status = e.response?.status ?? 0;
          const msg =
            status === 410
              ? 'This invitation has expired or has already been used.'
              : status === 404
                ? 'Invitation not found.'
                : 'Could not load this invitation.';
          setLoadError({ status, message: msg });
        }
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!token || !info) return;
    if (password !== confirm) return;
    setPending(true);
    const ok = await wrap(() =>
      api.post(`/v1/invitations/${token}/accept`, {
        password,
        password_confirmation: confirm,
      }),
    );
    setPending(false);
    if (ok) {
      setDone(true);
      // Send them to login on the surface they accepted from. (Email-link host
      // already routes them to admin.* or portal.* so /login works regardless.)
      setTimeout(() => navigate('/login'), 2200);
    }
  }

  if (loading) {
    return (
      <Centered>
        <p className="text-sm text-[var(--color-foreground-muted)]">Loading invitation…</p>
      </Centered>
    );
  }

  if (loadError) {
    return (
      <Centered>
        <Card>
          <CardHeader className="flex items-center gap-2">
            <AlertTriangle className="size-5 text-[var(--color-warning)]" />
            <h1 className="text-lg font-semibold">Invitation unavailable</h1>
          </CardHeader>
          <CardBody className="space-y-3">
            <p className="text-sm text-[var(--color-foreground-muted)]">{loadError.message}</p>
            <Button variant="secondary" onClick={() => navigate('/login')}>
              Go to sign in
            </Button>
          </CardBody>
        </Card>
      </Centered>
    );
  }

  if (done) {
    return (
      <Centered>
        <Card>
          <CardHeader className="flex items-center gap-2">
            <CheckCircle2 className="size-5 text-[var(--color-success)]" />
            <h1 className="text-lg font-semibold">Account created</h1>
          </CardHeader>
          <CardBody>
            <p className="text-sm text-[var(--color-foreground-muted)]">
              Redirecting to sign-in…
            </p>
          </CardBody>
        </Card>
      </Centered>
    );
  }

  if (!info) return null;

  const mismatched = confirm.length > 0 && password !== confirm;

  return (
    <Centered>
      <Card>
        <CardHeader>
          <div className="text-center">
            <h1 className="text-xl font-semibold tracking-tight">Welcome to Mehrutherm</h1>
            <p className="mt-1 text-sm text-[var(--color-foreground-muted)]">
              Set a password to finish creating your account.
            </p>
          </div>
        </CardHeader>
        <CardBody>
          <div className="mb-4 space-y-1 rounded-md border border-[var(--color-border)] bg-[var(--color-surface-muted)] p-3 text-sm">
            <div className="flex items-center gap-2">
              <span className="font-medium">{info.name}</span>
              <Badge tone="info">{info.role === 'admin' ? 'Admin' : 'Client'}</Badge>
            </div>
            <div className="text-xs text-[var(--color-foreground-muted)]">{info.email}</div>
          </div>

          <form className="space-y-3" onSubmit={handleSubmit}>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-[var(--color-foreground-muted)]">
                Password (min 12 chars, mixed case + a number)
              </span>
              <div className="relative">
                <Input
                  type={showPw ? 'text' : 'password'}
                  required
                  minLength={12}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  autoFocus
                  className="pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  className="absolute right-2 top-1/2 -translate-y-1/2 inline-flex size-6 items-center justify-center rounded text-[var(--color-foreground-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-foreground)]"
                >
                  {showPw ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                </button>
              </div>
            </label>
            <label className="block space-y-1">
              <span className="text-xs font-medium text-[var(--color-foreground-muted)]">
                Confirm password
              </span>
              <Input
                type={showPw ? 'text' : 'password'}
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                autoComplete="new-password"
              />
            </label>
            {mismatched ? (
              <p className="text-sm text-[var(--color-danger)]">Passwords don't match.</p>
            ) : null}
            {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
            <Button
              type="submit"
              className="w-full"
              disabled={pending || mismatched}
            >
              {pending ? 'Creating account…' : 'Create account'}
            </Button>
          </form>
        </CardBody>
      </Card>
    </Centered>
  );
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-full items-center justify-center p-6">
      <div className="w-full max-w-md">{children}</div>
    </div>
  );
}
